import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../api";
import { Modal } from "../components/ui";
import DocCountsTable from "../components/DocCountsTable";
import GestioneTable from "../components/GestioneTable";
import RevenueTransfers from "../components/RevenueTransfers";
import { MilestoneTrack } from "../components/MilestoneTrack";
import MarginInsights from "../components/MarginInsights";
import Outsourcing from "../components/Outsourcing";

const EUR = (n) => "€ " + Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmt = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString("it-IT"); } catch { return d; } };
const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function Collapse({ title, badge, children, defaultOpen = false, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="panel" data-testid={testid}>
      <div className="toolbar" style={{ cursor: "pointer", marginBottom: open ? undefined : 0 }} onClick={() => setOpen((o) => !o)} data-testid={testid ? `${testid}-toggle` : undefined}>
        <h3 style={{ margin: 0 }}>{title}</h3>
        <span className="muted" style={{ marginLeft: "auto" }}>{badge}</span>
        <button className="btn sm" type="button">{open ? "▲ Comprimi" : "▼ Espandi"}</button>
      </div>
      {open && children}
    </div>
  );
}

export default function Invoices() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [cfg, setCfg] = useState({ hourly_cost: 50, minutes_per_document: 15, target_growth: 0.30 });
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [obj, setObj] = useState(null);
  const [proj, setProj] = useState(null);
  const [searchCli, setSearchCli] = useState("");
  const fltCli = (list, cap) => {
    const l = list || [];
    if (searchCli.trim()) return l.filter((c) => (c.name || "").toLowerCase().includes(searchCli.trim().toLowerCase()));
    return cap ? l.slice(0, cap) : l;
  };
  const [movFile, setMovFile] = useState(null);
  const [movBusy, setMovBusy] = useState(false);
  const [moveInv, setMoveInv] = useState(null);
  const [moveTo, setMoveTo] = useState("");
  const [allCompanies, setAllCompanies] = useState([]);
  const [gest, setGest] = useState(null);
  const [invQ, setInvQ] = useState("");
  const [invYear, setInvYear] = useState("");
  const [invMonth, setInvMonth] = useState("");
  const [monthDet, setMonthDet] = useState(null);
  const [sp] = useSearchParams();
  const openMonth = async (m) => {
    try { const r = await api.get(`/invoices/month-detail?month=${m}`); setMonthDet(r.data); } catch { alert("Errore nel caricare il dettaglio del mese"); }
  };
  useEffect(() => {
    const m = parseInt(sp.get("mese") || "", 10);
    if (m >= 1 && m <= 12) openMonth(m);
  }, []);
  const openMove = (inv) => {
    setMoveInv(inv); setMoveTo("");
    if (allCompanies.length === 0) api.get("/companies").then((r) => setAllCompanies(r.data)).catch(() => {});
  };
  const doMove = async () => {
    try {
      const r = await api.put(`/invoices/${moveInv.id}/company`, { company_id: moveTo });
      alert(`Fattura ${moveInv.number} spostata su ${r.data.company}. Margini ricalcolati.`);
      setMoveInv(null); loadList(); loadObj(); loadProj();
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };

  const loadCfg = () => api.get("/invoices/config").then((r) => setCfg(r.data)).catch(() => {});
  const loadList = () => api.get("/invoices?limit=5000").then((r) => setList(r.data)).catch(() => {});
  const loadObj = () => api.get("/invoices/objective").then((r) => setObj(r.data)).catch(() => {});
  const loadProj = () => api.get("/invoices/projection").then((r) => setProj(r.data)).catch(() => {});
  const loadClients = () => api.get("/companies?limit=500").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => { loadCfg(); loadList(); loadObj(); loadProj(); loadClients(); }, []);

  const saveCfg = async () => {
    try {
      await api.put("/invoices/config", {
        hourly_cost: Number(cfg.hourly_cost),
        minutes_per_document: Number(cfg.minutes_per_document),
        minutes_per_email: Number(cfg.minutes_per_email ?? 5),
        minutes_per_task: Number(cfg.minutes_per_task ?? 20),
        minutes_first_whatsapp: Number(cfg.minutes_first_whatsapp ?? 10),
        minutes_per_whatsapp: Number(cfg.minutes_per_whatsapp ?? 2),
        target_growth: Number(cfg.target_growth),
      });
      await loadObj();
      loadClients();
      alert("Parametri salvati. Margini ricalcolati.");
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const reloadAll = () => { loadObj(); loadProj(); loadClients(); };

  const uploadMovements = async () => {
    if (!movFile) return;
    setMovBusy(true);
    const fd = new FormData();
    fd.append("file", movFile);
    try {
      const r = await api.post("/invoices/import-movements", fd, { headers: { "Content-Type": "multipart/form-data" } });
      alert(`Movimenti aggiornati per ${r.data.aggiornati} clienti.` + (r.data.non_trovati?.length ? `\nNon trovati: ${r.data.non_trovati.join(", ")}` : ""));
      setMovFile(null); reloadAll();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setMovBusy(false); }
  };

  const downloadMovTemplate = async () => {
    try {
      const r = await api.get("/invoices/movements-template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "template_movimenti.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/invoices/import-zip", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data); loadList(); reloadAll();
    } catch (e) {
      setResult({ error: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  return (
    <div>
      <h1 className="page-title">Margini clienti</h1>
      <p className="page-sub">Fatture emesse, registrazioni mensili e margini: carica lo zip delle fatture elettroniche o inserisci le registrazioni e i margini si aggiornano da soli</p>

      <div className="panel" data-testid="margins-kpi-panel">
        <h3 style={{ marginTop: 0 }}>📌 Ad oggi{gest ? ` · primi ${gest.months_elapsed} mesi ${gest.year}` : ""}</h3>
        <div className="dash-cards" style={{ marginBottom: 8 }}>
          <div className="dash-card" data-testid="kpi-revenue-ytd"><div className="num" style={{ fontSize: 24 }}>{gest ? EUR(gest.totals.revenue_ytd) : "…"}</div><div className="lbl">Fatturato (senza IVA)</div></div>
          <div className="dash-card" data-testid="kpi-margin-ytd"><div className="num" style={{ fontSize: 24, color: gest && gest.totals.margin_ytd < 0 ? "var(--danger)" : undefined }}>{gest ? EUR(gest.totals.margin_ytd) : "…"}</div><div className="lbl">Margine{gest && gest.months_elapsed ? ` · ${EUR(gest.totals.margin_ytd / gest.months_elapsed)}/mese` : ""}</div></div>
          <div className="dash-card" data-testid="kpi-hours-ytd">
            <div className="num" style={{ fontSize: 24 }}>{gest ? Number(gest.totals.hours_ytd).toLocaleString("it-IT", { maximumFractionDigits: 0 }) + " h" : "…"}</div>
            <div className="lbl">Ore dedicate ai clienti</div>
            {gest && gest.months_elapsed > 0 && (() => {
              const h1 = (n) => Number(n).toLocaleString("it-IT", { maximumFractionDigits: 1 });
              const perMonth = gest.totals.hours_ytd / gest.months_elapsed;
              return <div className="lbl" style={{ fontSize: 11, fontWeight: 500, marginTop: 3 }} data-testid="kpi-hours-avgs">≈ {h1(perMonth)} h/mese · {h1(perMonth / 4.345)} h/settimana · {h1(perMonth / 21.7)} h/giorno lavorativo</div>;
            })()}
          </div>
          <div className="dash-card" data-testid="kpi-docs-ytd"><div className="num" style={{ fontSize: 24 }}>{gest ? gest.totals.docs_ytd.toLocaleString("it-IT") : "…"}</div><div className="lbl">Registrazioni inserite</div></div>
          <div className="dash-card" data-testid="kpi-clients-count"><div className="num" style={{ fontSize: 24 }}>{gest ? gest.clients.length : "…"}</div><div className="lbl">Clienti con attività{proj ? ` · ${proj.counts.recurring} ricorrenti · ${proj.counts.new} nuovi · ${proj.counts.churned} persi` : ""}</div></div>
        </div>
        <h3 style={{ marginBottom: 8 }}>🔮 Proiezione fine anno</h3>
        <div className="dash-cards" style={{ marginBottom: 0 }}>
          <div className="dash-card" data-testid="kpi-proj-revenue"><div className="num" style={{ fontSize: 24 }}>{proj ? EUR(proj.projected_revenue) : "…"}</div><div className="lbl">Fatturato proiettato{proj ? ` · ricorrenti ${EUR(proj.projected_from_recurring)} + nuovi ${EUR(proj.projected_from_new)}` : ""}</div></div>
          <div className="dash-card" data-testid="kpi-proj-recurring"><div className="num" style={{ fontSize: 24 }}>{gest ? EUR(gest.totals.projected_recurring) : "…"}</div><div className="lbl">Proiezione solo clienti ricorrenti</div></div>
          <div className="dash-card" data-testid="kpi-target-revenue"><div className="num" style={{ fontSize: 24 }}>{proj ? EUR(proj.target_revenue) : "…"}</div><div className="lbl">Obiettivo fatturato{proj ? (proj.gap_to_target > 0 ? ` · mancano ${EUR(proj.gap_to_target)} rispetto alla proiezione (tendenziale)` : " · superato 🎉") : ""}</div></div>
          <div className="dash-card" data-testid="kpi-proj-margin"><div className="num" style={{ fontSize: 24 }}>{obj ? EUR(obj.projected_margin) : "…"}</div><div className="lbl">Margine proiettato · obiettivo {obj ? EUR(obj.target_margin) : "…"}</div></div>
          <div className="dash-card" data-testid="kpi-margin-gap"><div className="num" style={{ fontSize: 24, color: obj && obj.on_track_with_won ? "var(--green)" : "var(--danger)" }}>{obj ? (obj.on_track_with_won ? "✅ In linea" : "−" + EUR(obj.gap_after_won).replace("€ ", "€ ")) : "…"}</div><div className="lbl">{obj && !obj.on_track_with_won ? "Sotto l'obiettivo di margine" : "Rispetto all'obiettivo di margine"}</div></div>
        </div>
      </div>

      <MarginInsights />
      <Outsourcing />

      {obj && (
        <div className="panel" style={{ background: "linear-gradient(135deg,#5b8a8f,#7caab0)", color: "#fff" }}>
          <h3 style={{ color: "#fff", margin: 0 }}>🎯 Obiettivo annuo · superare del {Math.round((obj.growth || 0.30) * 100)}% il margine dell'anno scorso</h3>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 8 }}>
            <div style={{ fontSize: 34, fontWeight: 800 }} data-testid="objective-pct">{obj.progress_pct}%</div>
            <div>dell'obiettivo di periodo (primi {obj.months_elapsed} mesi)</div>
          </div>
          <div style={{ height: 12, borderRadius: 6, background: "rgba(255,255,255,.25)", overflow: "hidden", margin: "8px 0" }}>
            <div style={{ height: "100%", width: Math.min(100, obj.progress_pct) + "%", background: obj.progress_pct >= 100 ? "#cdeac0" : "#ffd98a", borderRadius: 6 }} />
          </div>
          <p style={{ margin: "4px 0" }}>Margine {obj.baseline_year}: <strong>{EUR(obj.baseline_margin)}</strong> → Obiettivo {obj.current_year}: <strong>{EUR(obj.target_margin)}</strong></p>
          <p style={{ margin: "4px 0" }}>Nei primi {obj.months_elapsed} mesi: <strong>{EUR(obj.current_margin)}</strong> · Obiettivo di periodo: <strong>{EUR(obj.prorata_target)}</strong></p>
          <p style={{ margin: "4px 0" }} data-testid="objective-projection">
            Di questo passo a fine anno: <strong>{EUR(obj.projected_margin)}</strong>
            {obj.won_revenue_total > 0 && <> · <strong>+{EUR(obj.won_revenue_total)}</strong> dai nuovi clienti chiusi nel funnel ({obj.won_revenue_items})</>}
            {" "}
            {obj.on_track_with_won
              ? <strong>✅ in linea per superare l'obiettivo</strong>
              : <strong>⚠️ sotto l'obiettivo di {EUR(obj.gap_after_won)}</strong>}
          </p>
        </div>
      )}

      {proj && (
        <div className="panel" data-testid="projection-2026-panel">
          <div className="toolbar">
            <h3 style={{ margin: 0 }}>📈 Proiezione {proj.target_year} · clienti ricorrenti</h3>
            <span className="muted" style={{ marginLeft: "auto" }}>
              Su {proj.months_elapsed} mesi di dati · annualizzato ×{proj.annualization_factor}
            </span>
          </div>
          <p className="muted" style={{ marginTop: 4 }}>
            Proiezione a 12 mesi calcolata dai dati {proj.target_year} disponibili, confrontata con l'obiettivo +{Math.round(proj.growth * 100)}% sul {proj.prev_year}.
          </p>

          <div className="grid-2" style={{ marginTop: 8 }}>
            <div className="field">
              <label>Fatturato proiettato {proj.target_year}</label>
              <div style={{ fontSize: 28, fontWeight: 800 }} data-testid="proj-revenue">{EUR(proj.projected_revenue)}</div>
              <span className="muted">Incassato finora: {EUR(proj.actual_revenue_ytd)}</span>
            </div>
            <div className="field">
              <label>Obiettivo {proj.target_year} (baseline {EUR(proj.baseline_revenue)})</label>
              <div style={{ fontSize: 28, fontWeight: 800 }} data-testid="proj-target">{EUR(proj.target_revenue)}</div>
              <span className="muted">{proj.gap_to_target > 0 ? `Manca rispetto alla proiezione (tendenziale): ${EUR(proj.gap_to_target)} · rispetto al garantito (prudente): ${EUR(proj.missing_to_target)}` : "Obiettivo superato 🎉"}</span>
            </div>
          </div>

          <div style={{ height: 12, borderRadius: 6, background: "rgba(255,255,255,.10)", overflow: "hidden", margin: "10px 0" }}>
            <div style={{ height: "100%", width: Math.min(100, proj.progress_pct) + "%", background: "var(--grad)", borderRadius: 6 }} />
          </div>
          <p style={{ margin: "4px 0" }}>Proiezione = <strong>{proj.progress_pct}%</strong> dell'obiettivo</p>
          <MilestoneTrack proj={proj} />

          <div className="grid-2" style={{ marginTop: 10 }}>
            <div>
              <p style={{ margin: "4px 0" }}>👥 <strong>{proj.counts.recurring}</strong> ricorrenti · <strong>{proj.counts.churned}</strong> persi · <strong>{proj.counts.new}</strong> nuovi</p>
              {proj.excel_sales?.lines > 0 && (
                <p style={{ margin: "4px 0" }} data-testid="proj-excel-sales">📗 Vendite file excel: <strong>{proj.excel_sales.lines}</strong> vendite · <strong>{proj.excel_sales.buyers}</strong> acquirenti · <strong style={{ color: "var(--green)" }}>{EUR(proj.excel_sales.total)}</strong></p>
              )}
              {proj.ytd_compare && (
                <div style={{ background: "rgba(34,211,238,.08)", border: "1px solid rgba(34,211,238,.35)", borderRadius: 8, padding: "10px 14px", margin: "10px 0" }} data-testid="proj-ytd-compare">
                  <strong>📈 Stesso periodo a confronto (1/1 → {proj.ytd_compare.as_of})</strong>
                  <p style={{ margin: "6px 0 0" }}>
                    {proj.target_year}: <strong>{EUR(proj.ytd_compare.current)}</strong> · {proj.prev_year}: <strong>{EUR(proj.ytd_compare.prev)}</strong> →{" "}
                    <strong style={{ color: proj.ytd_compare.delta >= 0 ? "var(--green)" : "#fb7185" }}>
                      {proj.ytd_compare.delta >= 0 ? "+" : ""}{EUR(proj.ytd_compare.delta)}
                      {proj.ytd_compare.delta_pct != null ? ` (${proj.ytd_compare.delta_pct >= 0 ? "+" : ""}${proj.ytd_compare.delta_pct}%)` : ""}
                    </strong>
                  </p>
                  <p style={{ margin: "4px 0 0" }}>
                    🎯 Passo necessario per il <strong>+{proj.ytd_compare.target_pct}%</strong> sul {proj.prev_year}: <strong>{EUR(proj.ytd_compare.pace_target)}</strong> nel periodo —{" "}
                    {proj.ytd_compare.gap_to_pace > 0
                      ? <>mancano <strong style={{ color: "#facc15" }}>{EUR(proj.ytd_compare.gap_to_pace)}</strong></>
                      : <strong style={{ color: "var(--green)" }}>passo superato 🎉</strong>}
                  </p>
                </div>
              )}
              {proj.monthly_compare && proj.monthly_compare.some((x) => x.prev || x.cur) && (() => {
                const max = Math.max(...proj.monthly_compare.map((x) => Math.max(x.prev, x.cur)), 1);
                const ML = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
                return (
                  <div style={{ margin: "12px 0 4px" }} data-testid="proj-monthly-chart">
                    <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                      Mese per mese: <span style={{ color: "#94a3b8" }}>■ {proj.prev_year}</span>{" "}
                      <span style={{ color: "#39ff14" }}>■ {proj.target_year} in vantaggio</span>{" "}
                      <span style={{ color: "#fb7185" }}>■ {proj.target_year} indietro</span>{" "}
                      · clicca un mese per il dettaglio fatture
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 120 }}>
                      {proj.monthly_compare.map((mc) => (
                        <div key={mc.m} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2, minWidth: 0, cursor: "pointer" }}
                          onClick={() => openMonth(mc.m)} data-testid={`month-bar-${mc.m}`}
                          title={`${ML[mc.m - 1]} — ${proj.prev_year}: ${EUR(mc.prev)} · ${proj.target_year}: ${EUR(mc.cur)} — clicca per il dettaglio`}>
                          <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 100, width: "100%", justifyContent: "center" }}>
                            <div style={{ width: "38%", height: `${Math.max((mc.prev / max) * 100, mc.prev ? 2 : 0)}%`, background: "#64748b", borderRadius: "2px 2px 0 0" }} />
                            <div style={{ width: "38%", height: `${Math.max((mc.cur / max) * 100, mc.cur ? 2 : 0)}%`, background: mc.cur >= mc.prev ? "#39ff14" : "#fb7185", borderRadius: "2px 2px 0 0" }} />
                          </div>
                          <span className="muted" style={{ fontSize: 10 }}>{ML[mc.m - 1]}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              <p style={{ margin: "4px 0" }}>📉 Ammanco dai clienti in calo: <strong style={{ color: "var(--danger)" }}>{EUR(proj.declining_shortfall)}</strong></p>
              <p style={{ margin: "4px 0" }}>🚪 Fatturato dei clienti persi: <strong style={{ color: "var(--danger)" }}>{EUR(proj.churned_revenue_lost)}</strong></p>
              <p style={{ margin: "4px 0" }}>📈 Surplus dai clienti in crescita: <strong style={{ color: "var(--green)" }}>{EUR(proj.growing_surplus)}</strong></p>
            </div>
            <div>
              <p style={{ margin: "4px 0" }}>Per centrare l'obiettivo, in alternativa:</p>
              <p style={{ margin: "4px 0" }}>➕ ~<strong>{proj.new_clients_needed} clienti nuovi</strong> (a {EUR(proj.avg_revenue_per_client_prev)} medi)</p>
              <p style={{ margin: "4px 0" }}>♻️ oppure recuperare <strong>{EUR(proj.gap_to_target)}</strong> dai clienti esistenti (persi + in calo)</p>
            </div>
          </div>

          <div className="field" style={{ maxWidth: 340, marginTop: 12 }}>
            <input value={searchCli} onChange={(e) => setSearchCli(e.target.value)}
              placeholder="🔍 Cerca cliente tra in calo / persi / nuovi…" data-testid="proj-search" />
          </div>

          {fltCli(proj.top_declining, 10).length > 0 && (
            <>
              <h4 style={{ marginBottom: 4 }}>Clienti in calo da recuperare</h4>
              <table data-testid="proj-declining-table">
                <thead><tr><th>Cliente</th><th>{proj.prev_year}</th><th>Proiezione {proj.target_year}</th><th>Differenza</th></tr></thead>
                <tbody>
                  {fltCli(proj.top_declining, 10).map((c) => (
                    <tr key={c.company_id}>
                      <td><Link to={`/aziende/${c.company_id}`}>{c.name}</Link></td>
                      <td>{EUR(c.rev_prev)}</td>
                      <td>{EUR(c.rev_projected)}</td>
                      <td style={{ color: "var(--danger)" }}>{EUR(c.delta_vs_prev)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {fltCli(proj.top_churned, 10).length > 0 && (
            <>
              <h4 style={{ marginBottom: 4 }}>Clienti persi nel {proj.target_year} (nessuna fattura finora)</h4>
              <table data-testid="proj-churned-table">
                <thead><tr><th>Cliente</th><th>Fatturato {proj.prev_year}</th></tr></thead>
                <tbody>
                  {fltCli(proj.top_churned, 10).map((c) => (
                    <tr key={c.company_id}>
                      <td><Link to={`/aziende/${c.company_id}`}>{c.name}</Link></td>
                      <td>{EUR(c.rev_prev)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {fltCli(proj.top_new).length > 0 && (
            <>
              <h4 style={{ marginBottom: 4 }}>Nuovi clienti {proj.target_year} ({proj.counts?.new ?? proj.top_new.length})</h4>
              <table data-testid="proj-new-table">
                <thead><tr><th>Cliente</th><th>Incassato finora</th><th>Proiezione {proj.target_year}</th></tr></thead>
                <tbody>
                  {fltCli(proj.top_new).map((c) => (
                    <tr key={c.company_id}>
                      <td><Link to={`/aziende/${c.company_id}`}>{c.name}</Link></td>
                      <td>{EUR(c.rev_actual)}</td>
                      <td style={{ color: "var(--green)" }}>{EUR(c.rev_projected)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <p className="muted" style={{ marginTop: 8 }}>
            ⚠️ La proiezione annualizza linearmente i mesi disponibili: i clienti con fatturazione annuale/anticipata (es. fatturati a dicembre per l'anno dopo) possono risultare sottostimati e vanno letti caso per caso.
          </p>
        </div>
      )}

      <GestioneTable onData={setGest} />

      <div className="panel">
        <h3>Carica fatture del mese</h3>
        <p className="muted">Seleziona il file .zip scaricato dal cassetto fiscale o dal gestionale (contiene i file .xml o .xml.p7m delle fatture emesse).</p>
        <div className="field"><label>Archivio fatture (.zip)</label>
          <input type="file" accept=".zip" onChange={(e) => setFile(e.target.files[0])} />
        </div>
        <button className="btn primary" onClick={upload} disabled={!file || busy}>{busy ? "Elaborazione…" : "Carica e aggiorna clienti"}</button>
      </div>

      <div className="panel">
        <h3>Movimenti / documenti per cliente</h3>
        <p className="muted">Carica un Excel/CSV con quanti movimenti hai registrato per ogni cliente (colonne: Ragione sociale e/o Partita IVA, e "Movimenti"). Servono a calcolare le ore dedicate e quindi il margine reale.</p>
        <div className="field"><label>File movimenti (.xlsx / .csv)</label>
          <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setMovFile(e.target.files[0])} />
        </div>
        <div className="toolbar">
          <button className="btn primary" onClick={uploadMovements} disabled={!movFile || movBusy}>{movBusy ? "Caricamento…" : "Carica movimenti"}</button>
          <button className="btn" onClick={downloadMovTemplate}>⬇ Scarica template movimenti</button>
        </div>
      </div>

      <div className="panel">
        <h3>Parametri di calcolo del margine</h3>
        <p className="muted">Margine cliente = fatturato − (ore dedicate × costo orario). Le ore dedicate derivano dai documenti gestiti e dagli appuntamenti a calendario. Se i documenti sono zero, contano solo le ore da calendario.</p>
        <div className="grid-2">
          <div className="field"><label>Costo orario dello studio (€/h)</label>
            <input type="number" min="0" value={cfg.hourly_cost} onChange={(e) => setCfg({ ...cfg, hourly_cost: e.target.value })} />
          </div>
          <div className="field"><label>Tempo medio per documento (minuti)</label>
            <input type="number" min="0" value={cfg.minutes_per_document} onChange={(e) => setCfg({ ...cfg, minutes_per_document: e.target.value })} />
          </div>
          <div className="field"><label>Tempo medio per email (minuti)</label>
            <input type="number" min="0" value={cfg.minutes_per_email ?? 5} onChange={(e) => setCfg({ ...cfg, minutes_per_email: e.target.value })} />
          </div>
          <div className="field"><label>Tempo medio per incarico su email (minuti)</label>
            <input type="number" min="0" value={cfg.minutes_per_task ?? 20} onChange={(e) => setCfg({ ...cfg, minutes_per_task: e.target.value })} />
          </div>
          <div className="field"><label>Primo WhatsApp della giornata (minuti)</label>
            <input type="number" min="0" value={cfg.minutes_first_whatsapp ?? 10} onChange={(e) => setCfg({ ...cfg, minutes_first_whatsapp: e.target.value })} data-testid="cfg-wa-first" />
          </div>
          <div className="field"><label>WhatsApp successivi (minuti l'uno)</label>
            <input type="number" min="0" value={cfg.minutes_per_whatsapp ?? 2} onChange={(e) => setCfg({ ...cfg, minutes_per_whatsapp: e.target.value })} data-testid="cfg-wa-next" />
          </div>
          <div className="field"><label>Obiettivo di crescita del margine (%)</label>
            <input type="number" min="0" value={Math.round((cfg.target_growth ?? 0.30) * 100)} onChange={(e) => setCfg({ ...cfg, target_growth: Number(e.target.value) / 100 })} />
          </div>
        </div>
        <button className="btn" onClick={saveCfg}>Salva e ricalcola margini</button>
      </div>

      {result && (
        <div className="panel">
          <h3>Risultato importazione</h3>
          {result.error ? (
            <p style={{ color: "var(--danger)" }}>{result.error}</p>
          ) : (
            <>
              <p>
                <strong style={{ color: "var(--green)" }}>{result.importate}</strong> fatture importate ·{" "}
                <strong>{result.doppioni_saltati}</strong> doppioni saltati ·{" "}
                <strong>{result.clienti_creati}</strong> clienti creati ·{" "}
                {result.fatture_lette} lette in totale
              </p>
              {result.errori?.length > 0 && (
                <ul className="muted">
                  {result.errori.map((e, i) => <li key={i}>{e.file}: {e.errore}</li>)}
                </ul>
              )}
            </>
          )}
        </div>
      )}

      <DocCountsTable />

      <RevenueTransfers />

      <Collapse title="📚 Margini per cliente (storico annuo)" testid="clients-margin-panel"
        badge={`${clients.filter((c) => c.annual_revenue != null || c.estimated_hours).length} clienti · ordinati per ore assorbite da email/incarichi`}>
        {clients.filter((c) => c.annual_revenue != null || c.estimated_hours).length === 0 ? (
          <p className="empty">Nessun margine calcolato. Carica le fatture per popolare i clienti.</p>
        ) : (
          <table>
            <thead><tr>
              <th>Cliente</th><th>Anno</th><th style={{ textAlign: "right" }}>Ricavo</th>
              <th style={{ textAlign: "right" }}>Email/incarichi</th>
              <th style={{ textAlign: "right" }}>Ore email+incarichi</th>
              <th style={{ textAlign: "right" }}>Ore totali</th>
              <th style={{ textAlign: "right" }}>Margine annuo</th>
            </tr></thead>
            <tbody>
              {[...clients]
                .filter((c) => c.annual_revenue != null || c.estimated_hours)
                .sort((a, b) => ((b.hours_emails || 0) + (b.hours_tasks || 0)) - ((a.hours_emails || 0) + (a.hours_tasks || 0)))
                .map((c) => {
                  const oreEmailTask = (c.hours_emails || 0) + (c.hours_tasks || 0);
                  return (
                    <tr key={c.id} data-testid="client-margin-row">
                      <td><Link to={`/aziende/${c.id}`}>{c.name}</Link></td>
                      <td className="muted">{c.reference_year || "—"}</td>
                      <td style={{ textAlign: "right" }}>{EUR(c.annual_revenue)}</td>
                      <td style={{ textAlign: "right" }} className="muted">{c.emails_used || 0} / {c.tasks_used || 0}</td>
                      <td style={{ textAlign: "right" }}>{oreEmailTask.toLocaleString("it-IT", { maximumFractionDigits: 1 })} h</td>
                      <td style={{ textAlign: "right" }}>{Number(c.estimated_hours || 0).toLocaleString("it-IT", { maximumFractionDigits: 1 })} h</td>
                      <td style={{ textAlign: "right", fontWeight: 700, color: (c.annual_margin || 0) >= 0 ? "var(--green)" : "var(--danger)" }}>{EUR(c.annual_margin)}</td>
                    </tr>
                  );
                })}
            </tbody>
            <tfoot>
              {(() => {
                const rows = clients.filter((c) => c.annual_revenue != null || c.estimated_hours);
                const sum = (f) => rows.reduce((s, c) => s + (Number(f(c)) || 0), 0);
                return (
                  <tr style={{ fontWeight: 700 }} data-testid="margins-totals-row">
                    <td>Totale ({rows.length} clienti)</td><td />
                    <td style={{ textAlign: "right" }}>{EUR(sum((c) => c.annual_revenue))}</td>
                    <td style={{ textAlign: "right" }}>{sum((c) => c.emails_used)} / {sum((c) => c.tasks_used)}</td>
                    <td style={{ textAlign: "right" }}>{sum((c) => (c.hours_emails || 0) + (c.hours_tasks || 0)).toLocaleString("it-IT", { maximumFractionDigits: 1 })} h</td>
                    <td style={{ textAlign: "right" }}>{sum((c) => c.estimated_hours).toLocaleString("it-IT", { maximumFractionDigits: 1 })} h</td>
                    <td style={{ textAlign: "right", color: sum((c) => c.annual_margin) >= 0 ? "var(--green)" : "var(--danger)" }}>{EUR(sum((c) => c.annual_margin))}</td>
                  </tr>
                );
              })()}
            </tfoot>
          </table>
        )}
      </Collapse>

      <Collapse title="🗂 Storico fatture importate" testid="invoices-history-panel" badge={`${list.length} fatture`}>
        {list.length === 0 ? <p className="empty">Nessuna fattura importata.</p> : (() => {
          const rows = list.filter((i) => {
            if (invQ.trim()) {
              const s = invQ.trim().toLowerCase();
              if (!((i.client_name || "").toLowerCase().includes(s) || (i.number || "").toLowerCase().includes(s))) return false;
            }
            if (invYear && (i.date || "").slice(0, 4) !== invYear) return false;
            if (invMonth && (i.date || "").slice(5, 7) !== invMonth) return false;
            return true;
          });
          const years = [...new Set(list.map((i) => (i.date || "").slice(0, 4)).filter(Boolean))].sort().reverse();
          const sumTax = rows.reduce((s, i) => s + (Number(i.taxable) || 0), 0);
          const sumTot = rows.reduce((s, i) => s + (Number(i.total) || 0), 0);
          return (
            <>
              <div className="toolbar" style={{ gap: 8, flexWrap: "wrap" }}>
                <input placeholder="Cerca cliente o numero…" value={invQ} onChange={(e) => setInvQ(e.target.value)} style={{ maxWidth: 240 }} data-testid="inv-filter-q" />
                <select value={invYear} onChange={(e) => setInvYear(e.target.value)} data-testid="inv-filter-year" style={{ maxWidth: 130 }}>
                  <option value="">Tutti gli anni</option>
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
                <select value={invMonth} onChange={(e) => setInvMonth(e.target.value)} data-testid="inv-filter-month" style={{ maxWidth: 150 }}>
                  <option value="">Tutti i mesi</option>
                  {MESI.map((m, i) => <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>)}
                </select>
                {(invQ || invYear || invMonth) && <button className="btn sm" onClick={() => { setInvQ(""); setInvYear(""); setInvMonth(""); }} data-testid="inv-filter-clear">✕ Pulisci filtri</button>}
                <span className="muted" style={{ marginLeft: "auto" }}>{rows.length} risultati</span>
              </div>
              <div className="dc-wrap" style={{ maxHeight: "60vh" }}>
                <table className="dc-sticky" data-testid="invoices-history-table">
                  <thead><tr><th>Data</th><th>Numero</th><th>Cliente</th><th style={{ textAlign: "right" }}>Imponibile</th><th style={{ textAlign: "right" }}>Totale</th><th></th></tr></thead>
                  <tbody>
                    {rows.map((i) => (
                      <tr key={i.id}>
                        <td>{fmt(i.date)}</td>
                        <td>{i.number || "—"}</td>
                        <td>{i.company_id ? <Link to={`/aziende/${i.company_id}`}>{i.client_name}</Link> : i.client_name}</td>
                        <td style={{ textAlign: "right" }}>{EUR(i.taxable)}</td>
                        <td style={{ textAlign: "right" }}>{EUR(i.total)}</td>
                        <td><button className="btn sm" onClick={() => openMove(i)} title="Sposta la fattura su un altro cliente" data-testid={`move-invoice-${i.id}`}>⇄ Sposta</button></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ fontWeight: 700 }} data-testid="invoices-totals-row">
                      <td>Totale ({rows.length} fatture)</td><td /><td />
                      <td style={{ textAlign: "right" }}>{EUR(sumTax)}</td>
                      <td style={{ textAlign: "right" }}>{EUR(sumTot)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          );
        })()}
      </Collapse>

      {moveInv && (
        <Modal title={`Sposta fattura ${moveInv.number || ""}`} onClose={() => setMoveInv(null)}>
          <p className="muted" style={{ marginTop: 0 }}>
            {fmt(moveInv.date)} · {EUR(moveInv.total)} · attualmente intestata a <b>{moveInv.client_name}</b>.
            Utile quando una fattura è stata intestata alla persona sbagliata (es. al socio invece che alla società).
          </p>
          <div className="field"><label>Sposta sul cliente</label>
            <select value={moveTo} onChange={(e) => setMoveTo(e.target.value)} data-testid="move-invoice-target">
              <option value="">— scegli cliente —</option>
              {allCompanies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setMoveInv(null)}>Annulla</button>
            <button className="btn primary" disabled={!moveTo} onClick={doMove} data-testid="move-invoice-confirm">Sposta e ricalcola</button>
          </div>
        </Modal>
      )}

      {monthDet && (
        <Modal title={`📅 ${MESI[monthDet.month - 1]} — ${monthDet.year} vs ${monthDet.prev_year}`} onClose={() => setMonthDet(null)}>
          <div data-testid="month-detail-modal">
            <p style={{ marginTop: 0 }}>
              {monthDet.year}: <strong>{EUR(monthDet.tot_cur)}</strong> · {monthDet.prev_year}: <strong>{EUR(monthDet.tot_prev)}</strong> →{" "}
              <strong style={{ color: monthDet.delta >= 0 ? "var(--green)" : "#fb7185" }}>
                {monthDet.delta >= 0 ? "+" : ""}{EUR(monthDet.delta)}
              </strong>
            </p>
            {monthDet.compare.length === 0 ? (
              <p className="muted">Nessuna fattura in questo mese.</p>
            ) : (
              <table data-testid="month-detail-compare">
                <thead><tr><th>Cliente</th><th>{monthDet.prev_year}</th><th>{monthDet.year}</th><th>Diff.</th></tr></thead>
                <tbody>
                  {monthDet.compare.map((c) => (
                    <tr key={c.company_id}>
                      <td>
                        {c.company_id ? <Link to={`/aziende/${c.company_id}`}>{c.name}</Link> : c.name}
                        {c.quota && <span className="muted" style={{ fontSize: 11 }}> · quota competenza annuale</span>}
                      </td>
                      <td>{c.prev ? EUR(c.prev) : "—"}</td>
                      <td>{c.cur ? EUR(c.cur) : "—"}</td>
                      <td style={{ color: c.delta >= 0 ? "var(--green)" : "#fb7185" }}>{c.delta >= 0 ? "+" : ""}{EUR(c.delta)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {[["cur", monthDet.year, monthDet.invoices_cur], ["prev", monthDet.prev_year, monthDet.invoices_prev]].map(([k, y, rows]) => rows.length > 0 && (
              <details key={k} style={{ marginTop: 10 }} data-testid={`month-detail-invoices-${k}`}>
                <summary style={{ cursor: "pointer" }}>Fatture {y} del mese ({rows.length})</summary>
                <table style={{ marginTop: 6 }}>
                  <thead><tr><th>N.</th><th>Data</th><th>Cliente</th><th>Imponibile</th></tr></thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td>{r.number}</td>
                        <td>{fmt(r.date)}</td>
                        <td>{r.company}</td>
                        <td>{EUR(r.taxable)}{r.quota && <span className="muted" style={{ fontSize: 11 }}> (quota 1/12 di {EUR(r.full_amount)})</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}
